import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/client.js';

/**
 * Billing capture.
 *
 * The blocks OCC_STAT assigns to the Case Administrator and the Billing Team
 * Leader, in the order the money actually moves: paperwork in → quote → invoice
 * → payment → and what happened when nobody paid.
 *
 * NOT AUTOSAVED, unlike the call card. That difference is deliberate. The call
 * card autosaves because a call can end mid-sentence and losing what was typed
 * is worse than anything else that can go wrong there. Billing is done sitting
 * down, after the fact, and an invoice number written to the database halfway
 * through being typed is a number that does not exist. Here an explicit save is
 * the safer default.
 */

interface Billing {
  paperwork_received: boolean;
  paperwork_received_by_billing: boolean;
  paperwork_number: string | null;
  treating_practitioner: string | null;
  debtor_name: string | null;
  debtor_phone_raw: string | null;
  debtor_email: string | null;
  needs_invoice: boolean | null;
  quoted_amount: string | null;
  quoted_by: string | null;
  paid_on_scene: boolean | null;
  payment_method: string | null;
  invoice_number: string | null;
  invoiced_at: string | null;
  invoiced_amount: string | null;
  invoiced_by: string | null;
  paid_at: string | null;
  amount_paid: string | null;
  non_payment_reason: string | null;
  handed_to_debt_collection: boolean;
  billing_notes: string | null;
  balance: string | null;
}

const INPUT =
  'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 ' +
  'placeholder:text-slate-500 focus:border-sky-500 focus:outline-none';
const LABEL = 'mb-1 block text-xs font-medium text-slate-300';

function money(v: string | null): string {
  if (v === null) return '';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '';
}

export function BillingPanel({ caseId }: { caseId: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ['billing', caseId],
    queryFn: () => api.get<{ billing: Billing | null }>(`/cases/${caseId}/billing`),
  });

  // Seed the form once from the server, and never again while the user is
  // typing — a refetch overwriting a half-typed invoice number is the classic
  // way a form silently loses work.
  useEffect(() => {
    if (!data?.billing || dirty) return;
    const b = data.billing;
    setForm({
      paperworkReceived: b.paperwork_received,
      paperworkReceivedByBilling: b.paperwork_received_by_billing,
      paperworkNumber: b.paperwork_number ?? '',
      treatingPractitioner: b.treating_practitioner ?? '',
      debtorName: b.debtor_name ?? '',
      debtorPhone: b.debtor_phone_raw ?? '',
      debtorEmail: b.debtor_email ?? '',
      needsInvoice: b.needs_invoice ?? false,
      quotedAmount: money(b.quoted_amount),
      quotedBy: b.quoted_by ?? '',
      paidOnScene: b.paid_on_scene ?? false,
      invoiceNumber: b.invoice_number ?? '',
      invoicedAt: b.invoiced_at ?? '',
      invoicedAmount: money(b.invoiced_amount),
      invoicedBy: b.invoiced_by ?? '',
      paidAt: b.paid_at ?? '',
      amountPaid: money(b.amount_paid),
      nonPaymentReason: b.non_payment_reason ?? '',
      handedToDebtCollection: b.handed_to_debt_collection,
      billingNotes: b.billing_notes ?? '',
    });
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () => {
      // Numbers go as numbers, or '' to clear. Sending "4500.50" as a string
      // would fail validation; sending 0 for an empty box would invent a quote
      // of nothing.
      const payload: Record<string, unknown> = { ...form };
      for (const k of ['quotedAmount', 'invoicedAmount', 'amountPaid']) {
        const v = form[k];
        payload[k] = v === '' || v === undefined ? '' : Number(v);
      }
      return api.patch(`/cases/${caseId}/billing`, payload);
    },
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ['billing', caseId] });
      void qc.invalidateQueries({ queryKey: ['case', caseId] });
    },
  });

  const set = (k: string, v: string | boolean) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const str = (k: string) => (typeof form[k] === 'string' ? form[k] : '');
  const bool = (k: string) => form[k] === true;

  const Text = ({
    k,
    label,
    placeholder,
    type = 'text',
  }: {
    k: string;
    label: string;
    placeholder?: string;
    type?: string;
  }) => (
    <div>
      <label className={LABEL}>{label}</label>
      <input
        className={INPUT}
        type={type}
        value={str(k)}
        placeholder={placeholder}
        onChange={(e) => set(k, e.target.value)}
      />
    </div>
  );

  const Check = ({ k, label }: { k: string; label: string }) => (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
      <input
        type="checkbox"
        checked={bool(k)}
        onChange={(e) => set(k, e.target.checked)}
        className="h-4 w-4 rounded border-slate-600 bg-slate-900"
      />
      {label}
    </label>
  );

  const balance = data?.billing?.balance ? Number(data.billing.balance) : 0;

  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Billing</h3>
        {balance > 0 && (
          <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-300">
            R {balance.toFixed(2)} outstanding
          </span>
        )}
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Check k="paperworkReceived" label="Paperwork received" />
          <Check k="paperworkReceivedByBilling" label="Paperwork received by billing" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Text k="paperworkNumber" label="Paperwork number" placeholder="eprf_5463" />
            <Text k="treatingPractitioner" label="Treating practitioner" />
          </div>
        </div>

        <div className="border-t border-line pt-3">
          <p className="mb-2 text-[11px] uppercase tracking-wider text-slate-500">
            Who is being billed
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Text k="debtorName" label="Debtor name" />
            <Text k="debtorPhone" label="Debtor phone" placeholder="082 123 4567" />
            <Text k="debtorEmail" label="Debtor email" type="email" />
          </div>
        </div>

        <div className="border-t border-line pt-3">
          <div className="mb-2 flex flex-wrap gap-4">
            <Check k="needsInvoice" label="Needs an invoice" />
            <Check k="paidOnScene" label="Paid on scene" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Text k="quotedAmount" label="Amount quoted" type="number" placeholder="0.00" />
            <Text k="quotedBy" label="Quoted by" />
          </div>
        </div>

        <div className="border-t border-line pt-3">
          <p className="mb-2 text-[11px] uppercase tracking-wider text-slate-500">Invoice</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Text k="invoiceNumber" label="Invoice number" />
            <Text k="invoicedAt" label="Date invoiced" type="date" />
            <Text k="invoicedAmount" label="Invoiced amount" type="number" placeholder="0.00" />
            <Text k="invoicedBy" label="Invoiced by" />
          </div>
        </div>

        <div className="border-t border-line pt-3">
          <p className="mb-2 text-[11px] uppercase tracking-wider text-slate-500">Payment</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Text k="paidAt" label="Date paid" type="date" />
            <Text k="amountPaid" label="Amount paid" type="number" placeholder="0.00" />
          </div>
        </div>

        <div className="border-t border-line pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Text k="nonPaymentReason" label="Reason for non-payment" />
            <div className="flex items-end pb-1.5">
              <Check k="handedToDebtCollection" label="Handed to debt collection" />
            </div>
          </div>
          <div className="mt-3">
            <label className={LABEL}>Notes</label>
            <textarea
              className={`${INPUT} min-h-[68px] resize-y`}
              value={str('billingNotes')}
              placeholder="Conversations, follow-ups, motivations"
              onChange={(e) => set('billingNotes', e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3 border-t border-line pt-3">
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="rounded bg-sky-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save billing'}
          </button>
          {dirty && !save.isPending && (
            <span className="text-xs text-amber-400">Unsaved changes</span>
          )}
          {save.isError && <span className="text-xs text-red-400">{save.error.message}</span>}
        </div>
      </div>
    </div>
  );
}
